import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Clock, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { fetchBlogs } from '../lib/api';

const CATS = ['All', 'Exam Prep', 'College Guide', 'News', 'Career', 'Scholarships', 'Guide'];

const Blog = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    fetchBlogs().then(setBlogs).catch(() => setBlogs([]));
  }, []);

  const filtered = blogs.filter((b) => {
    const matchesQ = !q || b.title.toLowerCase().includes(q.toLowerCase());
    const matchesC = cat === 'All' || b.category === cat;
    return matchesQ && matchesC;
  });

  const featured = filtered[0];

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Learndunia Blog</h1>
          <p className="text-slate-600 mt-1">Insights, guides and news for students &amp; parents.</p>
          <div className="mt-6 relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles..." className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm bg-white" />
          </div>
          <div className="flex flex-wrap gap-2 mt-5">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${cat === c ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-teal-50 hover:text-teal-700'}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {featured && (
          <Link to={`/blog/${featured.slug || featured.id}`} className="group grid lg:grid-cols-2 gap-6 bg-white rounded-2xl overflow-hidden border border-slate-200 card-lift mb-8">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-orange-200 self-start mb-3">Featured</Badge>
              <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                <span className="font-semibold text-teal-700">{featured.category}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {featured.readTime}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 group-hover:text-teal-700 transition-colors leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{featured.title}</h2>
              <p className="text-slate-600 mt-3">{featured.excerpt}</p>
              <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {featured.author}</span>
                <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {featured.date}</span>
              </div>
            </div>
          </Link>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.slice(1).map((b) => (
            <Link key={b.id} to={`/blog/${b.slug || b.id}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-200 card-lift">
              <div className="aspect-[16/9] overflow-hidden">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold">{b.category}</span>
                  <span className="text-slate-500">· {b.readTime}</span>
                </div>
                <h3 className="mt-3 font-semibold text-slate-900 text-lg leading-snug group-hover:text-teal-700 transition-colors line-clamp-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">{b.excerpt}</p>
                <div className="mt-4 text-xs text-slate-500">By {b.author} · {b.date}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Blog;
